import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Package,
  CreditCard,
  Receipt,
  Megaphone,
  ScrollText,
  LifeBuoy,
  ArrowLeft,
  LogOut,
  X, Layers } from "lucide-react";
import Logo from "../shared/brand/Logo";
import { useAuth } from "../features/auth/context/AuthContext";
import MobileTabBar from "./components/MobileTabBar";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/workspaces", label: "Workspaces", icon: Building2 },
  { to: "/admin/plans", label: "Plans", icon: Package },
  { to: "/admin/presets", label: "Preset library", icon: Layers },
  { to: "/admin/billing", label: "Billing", icon: CreditCard },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  { to: "/admin/broadcasts", label: "Broadcasts", icon: Megaphone },
  { to: "/admin/support", label: "Support", icon: LifeBuoy },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText },
];

// Quick links in the mobile bottom bar (the rest live behind the hamburger).
const MOBILE_TABS = [
  { to: "/admin", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/plans", label: "Plans", icon: Package },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt },
];

/** Super-admin console shell. Mirrors VaultLayout but with the admin nav set. */
export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  return (
    <div className="vault-surface flex h-screen text-ink-900 md:gap-4 md:p-3">
      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 backdrop-blur-sm md:hidden"
          style={{ background: "rgba(48,38,22,.28)" }}
          onClick={() => setDrawerOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={`glass-rim fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col transition-transform duration-300 md:static md:z-auto md:w-[248px] md:translate-x-0 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        style={{
          background: "rgba(255,255,255,.30)",
          backdropFilter: "blur(40px) saturate(1.6)",
          WebkitBackdropFilter: "blur(40px) saturate(1.6)",
          border: "1px solid rgba(255,255,255,.50)",
          borderRadius: "28px",
          boxShadow: "var(--shadow-glass), var(--inset-hi)",
        }}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <Logo size="sm" showTagline tagline="Admin" />
          <button onClick={() => setDrawerOpen(false)} className="rounded-lg p-1.5 text-ink-400 hover:bg-white/45 md:hidden" aria-label="Close menu">
            <X size={18} />
          </button>
        </div>

        <button
          onClick={() => navigate("/vault")}
          className="mx-3.5 mt-1 flex items-center gap-2 rounded-full border border-white/50 bg-white/32 px-3 py-2 text-[12.5px] font-medium text-ink-600 backdrop-blur transition hover:bg-white/55 hover:text-ink-900"
        >
          <ArrowLeft size={14} /> Back to Vault
        </button>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3.5 pt-4">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) =>
                `nav-item flex items-center gap-3 rounded-[14px] px-3.5 py-2.5 text-[13px] font-medium transition ${
                  isActive ? "is-selected" : "border border-transparent text-ink-600 hover:bg-white/45 hover:text-ink-900"
                }`
              }
            >
              <Icon size={17} className="relative z-[1]" />
              <span className="relative z-[1]">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3.5">
          <div className="flex items-center gap-2.5 rounded-xl px-2 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-900 text-[12px] font-semibold text-white">
              {(user?.name ?? "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] font-semibold text-ink-900">{user?.name}</p>
              <p className="truncate text-[11px] text-ink-400">Super admin</p>
            </div>
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="rounded-lg p-1.5 text-ink-400 transition-colors hover:bg-ink-50 hover:text-ink-800"
              title="Sign out"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto pb-16 md:rounded-[24px] md:pb-0">
        <Outlet />
      </main>

      <MobileTabBar items={MOBILE_TABS} onMenu={() => setDrawerOpen(true)} />
    </div>
  );
}
